import React from 'react';
import SEO from '../../components/SEO';

const CaseStudy1 = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <SEO
        title="Indoor Routing Map Navigator — Case Study | STRAY Apps"
        description="An intelligent indoor navigation system that leverages AI to automatically detect spaces from floor plans and provides interactive routing capabilities."
        path="/case-studies/casestudy1"
      />
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                Indoor Routing Map Navigator
              </h1>
              <p className="text-purple-400 text-lg">AI-Powered Indoor Navigation Solution</p>
            </div>
          </div>
        </div>

        {/* Overview */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">Overview</h2>
          <p className="text-gray-300 text-lg leading-relaxed">
            An intelligent indoor navigation system that leverages AI to automatically detect spaces
            from floor plans and provides interactive routing capabilities. This solution transforms
            static floor plans into dynamic, navigable maps with seamless integration to popular mapping
            and communication platforms.
          </p>
        </section>

        {/* Key Features */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-6">Key Features</h2>
          <div className="grid gap-4">
            <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-xl p-6 border border-purple-500/30 flex items-start gap-4">
              
              <div>
                <h3 className="text-xl font-semibold text-purple-300 mb-2">
                  AI-powered Space Detection from Floor Plans
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Advanced machine learning algorithms automatically identify and map rooms, corridors,
                  and spaces from uploaded floor plans.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-xl p-6 border border-purple-500/30 flex items-start gap-4">
              <div>
                <h3 className="text-xl font-semibold text-purple-300 mb-2">
                  Interactive Canvas with Drag-and-Drop Editing
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Intuitive interface allowing users to refine and customize detected spaces with
                  real-time visual feedback.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-xl p-6 border border-purple-500/30 flex items-start gap-4">
              
              <div>
                <h3 className="text-xl font-semibold text-purple-300 mb-2">
                  Polygon-based Room Boundary Detection
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Precise boundary detection using polygon mapping for accurate representation of
                  complex room shapes and layouts.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-xl p-6 border border-purple-500/30 flex items-start gap-4">
              
              <div>
                <h3 className="text-xl font-semibold text-purple-300 mb-2">
                  Real-time Coordinate Transformation
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Dynamic coordinate system that enables seamless conversion between floor plan
                  coordinates and navigation systems.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-xl p-6 border border-purple-500/30 flex items-start gap-4">
              
              <div>
                <h3 className="text-xl font-semibold text-purple-300 mb-2">
                  WhatsApp Business API Integration
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Send location information and directions directly through WhatsApp for easy
                  communication with visitors and staff.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-xl p-6 border border-purple-500/30 flex items-start gap-4">
              
              <div>
                <h3 className="text-xl font-semibold text-purple-300 mb-2">
                  Google Maps Compatibility
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Seamless integration with Google Maps for transitioning between outdoor and
                  indoor navigation experiences.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Demo Section - Video */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-6">Demo</h2>
          <div className="rounded-2xl overflow-hidden border-2 border-purple-500/30 bg-slate-800/50">
            <video
              controls
              className="w-full"
            >
              <source src="/videos/case-study-1/demo.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <p className="text-gray-400 text-sm mt-3 text-center">
            Watch the indoor navigation system in action
          </p>
        </section>

        {/* Technical Stack */}
        <section className="mb-12 bg-slate-800/50 rounded-xl p-8 border border-purple-500/20">
          <h2 className="text-3xl font-bold text-white mb-6">Technical Implementation</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-semibold text-purple-300 mb-3">Core Technologies</h3>
              <ul className="text-gray-300 space-y-2">
                <li>• Machine Learning for space detection</li>
                <li>• Canvas API for interactive editing</li>
                <li>• WebSocket for real-time updates</li>
                <li>• RESTful API architecture</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-300 mb-3">Integrations</h3>
              <ul className="text-gray-300 space-y-2">
                <li>• WhatsApp Business API</li>
                <li>• Google Maps Platform</li>
                <li>• Cloud storage for floor plans</li>
                <li>• Analytics and tracking</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-6">Use Cases</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-xl p-6 border border-purple-500/40">
              <h3 className="text-xl font-semibold text-white mb-3">Corporate Campuses</h3>
              <p className="text-gray-300 leading-relaxed">
                Help employees and visitors navigate large office buildings and find meeting
                rooms, departments, and amenities.
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-xl p-6 border border-purple-500/40">
              <h3 className="text-xl font-semibold text-white mb-3">Shopping Malls</h3>
              <p className="text-gray-300 leading-relaxed">
                Guide shoppers to stores, restaurants, and facilities with turn-by-turn
                indoor navigation.
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-xl p-6 border border-purple-500/40">
              <h3 className="text-xl font-semibold text-white mb-3">Hospitals & Healthcare</h3>
              <p className="text-gray-300 leading-relaxed">
                Direct patients and visitors to departments, rooms, and emergency exits in
                complex medical facilities.
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-xl p-6 border border-purple-500/40">
              <h3 className="text-xl font-semibold text-white mb-3">Universities</h3>
              <p className="text-gray-300 leading-relaxed">
                Help students and visitors find classrooms, labs, offices, and campus
                facilities across multiple buildings.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CaseStudy1;